import { useEffect, useState } from 'react';
import { api, type AccountDetail, type PackageView } from '../api.ts';
import { limit, message, overLimit } from './Platform.tsx';
import { Operators } from './Operators.tsx';

interface AccountScreenProps {
  accountId: string;
  onBack: () => void;
}

const STATES = ['trial', 'active', 'past_due', 'suspended', 'closed'] as const;

/**
 * One client in full: who works there, what they have connected, what they are
 * allowed, and how much of it they have used.
 *
 * The usage-against-limit rows are the point of the screen. A number on its own
 * is trivia; a number beside the limit it is measured against is the difference
 * between "eleven tenants" and "eleven tenants on a package that allows ten,
 * which is why nothing has been assessed since Tuesday".
 */
export const AccountScreen = ({ accountId, onBack }: AccountScreenProps) => {
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [packages, setPackages] = useState<PackageView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [state, setState] = useState<string>('active');
  const [stateDetail, setStateDetail] = useState('');
  const [packageId, setPackageId] = useState('');
  const [note, setNote] = useState('');

  const load = () => {
    void Promise.all([api.platformAccount(accountId), api.platformPackages()])
      .then(([account, catalogue]) => {
        setDetail(account);
        setPackages(catalogue.packages);
        setState(account.account.state);
        setStateDetail(account.account.stateDetail ?? '');
      })
      .catch((cause: unknown) => setError(message(cause)));
  };

  useEffect(load, [accountId]);

  const act = async (what: () => Promise<unknown>, said: string) => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await what();
      setStatus(said);
      load();
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  if (error !== null && detail === null) return <p className="error-banner">{error}</p>;
  if (detail === null) return <p className="note">Loading client…</p>;

  const { account, usage, subscription } = detail;
  const allowance = subscription;

  const rows: Array<{ label: string; used: number; allowed: number | null }> = [
    { label: 'Connected tenants', used: usage.tenants, allowed: allowance?.maxTenants ?? null },
    {
      label: 'Tenants with scheduled assessment on',
      used: usage.schedules,
      allowed: allowance?.maxSchedules ?? null,
    },
    { label: 'Active operators', used: usage.seats, allowed: allowance?.maxSeats ?? null },
  ];

  const stopped = rows.some((row) => overLimit(row.used, row.allowed));

  return (
    <section className="pf-stack">
      <div className="toolbar">
        <button type="button" className="btn" onClick={onBack}>
          ← All clients
        </button>
        {status !== null && (
          <span className="status-inline" role="status">
            {status}
          </span>
        )}
      </div>

      {error !== null && <p className="error-banner">{error}</p>}

      {stopped && (
        <div className="callout callout-warn">
          This client is over a limit on its package, so <strong>none of its tenants is being
          assessed</strong> until the package or the estate changes. Everything already collected is
          untouched.
        </div>
      )}

      {(account.state === 'suspended' || account.state === 'closed') && (
        <div className="callout callout-warn">
          This client is {account.state}. Its operators cannot sign in and none of its tenants is
          being assessed. {account.stateDetail ?? ''}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2>{account.name}</h2>
        </div>
        <div className="card-body">
          <dl className="pf-facts">
            <div>
              <dt>State</dt>
              <dd>{account.state}</dd>
            </div>
            <div>
              <dt>Billing email</dt>
              <dd>{account.billingEmail ?? '—'}</dd>
            </div>
            <div>
              <dt>Package</dt>
              <dd>{subscription?.packageName ?? 'None — unlimited'}</dd>
            </div>
            <div>
              <dt>Client since</dt>
              <dd>{account.createdAt.slice(0, 10)}</dd>
            </div>
          </dl>

          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Allowance</th>
                  <th>Used</th>
                  <th>Allowed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className={overLimit(row.used, row.allowed) ? 'pf-over' : undefined}>
                    <td>{row.label}</td>
                    <td>{row.used}</td>
                    <td>{limit(row.allowed)}</td>
                  </tr>
                ))}
                <tr>
                  <td>Assessments per tenant per month</td>
                  <td>—</td>
                  <td>{limit(allowance?.maxScansPerTenantPeriod ?? null)}</td>
                </tr>
                <tr>
                  <td>Frameworks</td>
                  <td>—</td>
                  <td>{allowance?.frameworks?.join(', ') ?? 'All'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Tenants ({detail.tenants.length})</h2>
        </div>
        <div className="card-body">
          {detail.tenants.length === 0 ? (
            <p className="note">This client has not connected a tenant yet.</p>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Entra tenant id</th>
                    <th>Connection</th>
                    <th>Scheduled</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.tenants.map((tenant) => (
                    <tr key={tenant.id}>
                      <td>
                        {tenant.displayName}
                        {tenant.archivedAt !== null && <span className="hint"> archived</span>}
                      </td>
                      <td className="mono">{tenant.entraTenantId}</td>
                      <td>{tenant.connectionState}</td>
                      <td>{tenant.archivedAt === null ? 'yes' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Operators
        accountId={accountId}
        accountState={account.state}
        users={detail.users}
        onChanged={load}
      />

      <div className="card">
        <div className="card-head">
          <h2>Package</h2>
        </div>
        <div className="card-body">
          <p className="lead">
            Assigning copies the package’s numbers onto this client, and they stay in step: editing
            the package later changes this client too. The history below is what they were entitled
            to and when, which is what a billing question is made of.
          </p>
          <div className="td-fields">
            <div className="field">
              <label htmlFor="pf-package">Package</label>
              <select
                id="pf-package"
                value={packageId}
                onChange={(event) => setPackageId(event.target.value)}
              >
                <option value="">Choose…</option>
                {packages
                  .filter((entry) => entry.retiredAt === null)
                  .map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="pf-note">Note</label>
              <input id="pf-note" value={note} onChange={(event) => setNote(event.target.value)} />
              <span className="hint">Why, in one line. It travels with the subscription.</span>
            </div>
          </div>
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || packageId === ''}
              onClick={() =>
                void act(
                  () =>
                    api.assignSubscription(accountId, {
                      packageId,
                      note: note.trim() === '' ? null : note.trim(),
                    }),
                  'Assigned. It takes effect immediately.',
                )
              }
            >
              Assign package
            </button>
          </div>

          {detail.history.length > 0 && (
            <div className="scroll pf-history">
              <table>
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.history.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.packageName}</td>
                      <td>{entry.startsAt.slice(0, 10)}</td>
                      <td>{entry.endsAt?.slice(0, 10) ?? 'current'}</td>
                      <td>{entry.note ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>State</h2>
        </div>
        <div className="card-body">
          <p className="lead">
            Suspending stops every operator of this client signing in on their next request, and
            stops every one of their tenants being assessed. <strong>Past due does not</strong> — an
            unpaid invoice is a conversation, not a reason to put a gap in somebody’s audit
            evidence.
          </p>
          <div className="td-fields">
            <div className="field">
              <label htmlFor="pf-state">State</label>
              <select id="pf-state" value={state} onChange={(event) => setState(event.target.value)}>
                {STATES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="pf-state-detail">Reason</label>
              <input
                id="pf-state-detail"
                value={stateDetail}
                onChange={(event) => setStateDetail(event.target.value)}
              />
              <span className="hint">
                Stored with the decision. An account suspended in March and still suspended in July
                should not need anybody to remember why.
              </span>
            </div>
          </div>
          <div className="toolbar">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                void act(
                  () =>
                    api.setAccountState(accountId, {
                      state,
                      detail: stateDetail.trim() === '' ? null : stateDetail.trim(),
                    }),
                  'Saved.',
                )
              }
            >
              Save state
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
